import React, { useEffect, useState } from 'react';
import { fetchAcrRepositories, fetchAcrTags, fetchAllAcrTags, bulkDeploy } from '../api/deploymentApi';
import {
    CloudArrowUpIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface AcrState {
    loading: boolean;
    error: string | null;
    available: boolean;
}

const DeploymentPage = () => {
    const [acrState, setAcrState] = useState<AcrState>({ loading: true, error: null, available: false });
    const [repositories, setRepositories] = useState<string[]>([]);
    const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
    const [repoTags, setRepoTags] = useState<string[]>([]);
    const [repoTagsLoading, setRepoTagsLoading] = useState(false);
    const [allTags, setAllTags] = useState<{ repository: string; tag: string }[]>([]);
    const [selectedImages, setSelectedImages] = useState<{ repository: string; tag: string }[]>([]);
    const [namespace, setNamespace] = useState('');
    const [cluster, setCluster] = useState('');
    const [deployStatus, setDeployStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message: string }>({ type: 'idle', message: '' });

    const loadAcrData = async () => {
        setAcrState({ loading: true, error: null, available: false });
        try {
            const [repos, tags] = await Promise.all([
                fetchAcrRepositories(),
                fetchAllAcrTags(),
            ]);
            setRepositories(repos ?? []);
            setAllTags(tags ?? []);
            setAcrState({ loading: false, error: null, available: true });
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? 'Unknown error';
            setAcrState({ loading: false, error: msg, available: false });
        }
    };

    useEffect(() => {
        loadAcrData();
    }, []);

    const handleRepoClick = async (repo: string) => {
        setSelectedRepo(repo);
        setRepoTags([]);
        setRepoTagsLoading(true);
        try {
            const tags = await fetchAcrTags(repo);
            setRepoTags(tags ?? []);
        } catch {
            setRepoTags([]);
        } finally {
            setRepoTagsLoading(false);
        }
    };

    const handleTagSelect = (repo: string, tag: string) => {
        setSelectedImages(prev =>
            prev.some(img => img.repository === repo && img.tag === tag)
                ? prev.filter(img => !(img.repository === repo && img.tag === tag))
                : [...prev, { repository: repo, tag }]
        );
    };

    const handleBulkDeploy = async () => {
        setDeployStatus({ type: 'loading', message: 'Deploying...' });
        try {
            await bulkDeploy({ cluster, namespace, images: selectedImages });
            setDeployStatus({ type: 'success', message: `Successfully deployed ${selectedImages.length} image(s) to ${namespace}.` });
            setSelectedImages([]);
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? 'Unknown error';
            setDeployStatus({ type: 'error', message: `Deployment failed: ${msg}` });
        }
    };

    // ── Error / not-configured state ────────────────────────────────────────
    if (acrState.loading) {
        return (
            <div className="px-6 py-6">
                <div className="flex items-center gap-2 mb-6">
                    <CloudArrowUpIcon className="h-6 w-6 text-gray-500" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Image Deploy</h1>
                </div>
                <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    <span>Connecting to Azure Container Registry…</span>
                </div>
            </div>
        );
    }

    if (!acrState.available) {
        return (
            <div className="px-6 py-6">
                <div className="flex items-center gap-2 mb-6">
                    <CloudArrowUpIcon className="h-6 w-6 text-gray-500" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Image Deploy</h1>
                </div>
                <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-6">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Azure Container Registry not available</h3>
                            <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
                                ACR access requires Azure credentials (<code className="font-mono bg-amber-100 dark:bg-amber-800 px-1 rounded">DefaultAzureCredential</code>).
                                In local development, this requires an Azure CLI login or managed identity.
                            </p>
                            {acrState.error && (
                                <pre className="text-xs font-mono bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300 p-3 rounded overflow-auto max-h-32">
                                    {acrState.error}
                                </pre>
                            )}
                            <div className="mt-4 flex gap-3">
                                <button
                                    onClick={loadAcrData}
                                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800 transition-colors"
                                >
                                    <ArrowPathIcon className="h-4 w-4" />
                                    Retry
                                </button>
                                <span className="text-xs text-amber-500 dark:text-amber-500 self-center">
                                    Run <code className="font-mono">az login</code> then retry
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Future DR section placeholder */}
                <div className="mt-8 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-6 text-center text-gray-400 dark:text-gray-600">
                    <CloudArrowUpIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm font-medium">Full environment deploy &amp; disaster recovery</p>
                    <p className="text-xs mt-1">Deploy a full application stack to a new environment or Kubernetes cluster — coming soon.</p>
                </div>
            </div>
        );
    }

    // ── Main UI ──────────────────────────────────────────────────────────────
    return (
        <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <CloudArrowUpIcon className="h-6 w-6 text-gray-500" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Image Deploy</h1>
                </div>
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>ACR connected — <span className="font-medium">ontoriscentralacr</span></span>
                    <button onClick={loadAcrData} title="Refresh" className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <ArrowPathIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Repositories */}
                <div className="lg:w-1/3">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Repositories ({repositories.length})
                    </h2>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                        {repositories.length === 0 ? (
                            <p className="p-4 text-sm text-gray-400">No repositories found.</p>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {repositories.map(repo => (
                                    <li
                                        key={repo}
                                        onClick={() => handleRepoClick(repo)}
                                        className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                            selectedRepo === repo
                                                ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 font-medium'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        {repo}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Tags for selected repo */}
                <div className="lg:w-1/3">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        {selectedRepo ? `Tags — ${selectedRepo}` : 'Select a repository'}
                    </h2>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 min-h-[120px]">
                        {!selectedRepo ? (
                            <p className="p-4 text-sm text-gray-400">Click a repository to browse its tags.</p>
                        ) : repoTagsLoading ? (
                            <div className="flex items-center gap-2 p-4 text-sm text-gray-400">
                                <ArrowPathIcon className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        ) : repoTags.length === 0 ? (
                            <p className="p-4 text-sm text-gray-400">No tags found.</p>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {repoTags.map(tag => (
                                    <li key={tag} className="px-4 py-2 text-sm">
                                        <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-primary-600"
                                                checked={selectedImages.some(img => img.repository === selectedRepo && img.tag === tag)}
                                                onChange={() => handleTagSelect(selectedRepo, tag)}
                                            />
                                            <span className="font-mono text-xs">{tag}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Selected images */}
                <div className="lg:w-1/3">
                    <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Selected Images ({selectedImages.length})
                    </h2>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 min-h-[120px]">
                        {selectedImages.length === 0 ? (
                            <p className="p-4 text-sm text-gray-400">No images selected.</p>
                        ) : (
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {selectedImages.map(({ repository, tag }) => (
                                    <li key={`${repository}:${tag}`} className="px-4 py-2 flex items-center justify-between text-sm">
                                        <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate">
                                            {repository}:<span className="text-primary-600 dark:text-primary-400">{tag}</span>
                                        </span>
                                        <button
                                            onClick={() => handleTagSelect(repository, tag)}
                                            className="ml-2 text-gray-400 hover:text-red-500 text-xs flex-shrink-0"
                                        >
                                            ✕
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Deploy controls */}
            <div className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Deploy to Kubernetes</h2>
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cluster</label>
                        <input
                            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. ontoris-aks-prod"
                            value={cluster}
                            onChange={e => setCluster(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Namespace</label>
                        <input
                            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. default"
                            value={namespace}
                            onChange={e => setNamespace(e.target.value)}
                        />
                    </div>
                    <button
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded text-sm font-medium transition-colors"
                        onClick={handleBulkDeploy}
                        disabled={selectedImages.length === 0 || !namespace || !cluster || deployStatus.type === 'loading'}
                    >
                        {deployStatus.type === 'loading' ? (
                            <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Deploying…</>
                        ) : (
                            <><CloudArrowUpIcon className="h-4 w-4" /> Deploy {selectedImages.length || ''} Image{selectedImages.length !== 1 ? 's' : ''}</>
                        )}
                    </button>
                </div>

                {deployStatus.type !== 'idle' && (
                    <div className={`mt-3 text-sm px-3 py-2 rounded ${
                        deployStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' :
                        deployStatus.type === 'error'   ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' :
                        'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                        {deployStatus.message}
                    </div>
                )}
            </div>

            {/* Future DR section placeholder */}
            <div className="mt-6 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-5 text-center text-gray-400 dark:text-gray-600">
                <CloudArrowUpIcon className="h-7 w-7 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">Full environment deploy &amp; disaster recovery</p>
                <p className="text-xs mt-1">Deploy a full application stack to a new environment — coming soon.</p>
            </div>
        </div>
    );
};

export default DeploymentPage;
